import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { upsellTransition, UpsellTransitionStages } from '@ecs-uk/web-common-mobile-view-helpers';
import { trackPaymentMethodTokenizationResult, trackSwitchFormAttempt } from '../../helpers/switch/analytics';
import { PAYMENT_LOG_STEPS } from '../../constants/paymentLogSteps';
import useTrackUpsellStage from '../PaymentOrchestrator/hooks/useTrackUpsellStage';
import { PaymentContext } from '../PaymentOrchestrator/PaymentContextProvider';
import paymentMethodStoredTransition from '../PaymentOrchestrator/transitions/paymentMethodStoredTransition';
import { PAYMENT_ERROR_GROUPS, PAYMENT_ERROR_TYPES } from '../PaymentOrchestrator/constants/paymentErrors';
import useSplits from '../../hooks/useSplits';
import { splitsList } from '../../constants';
import PaymentError from './PaymentError';
import PaymentHeading from './PaymentHeading';
import PaymentReview from './PaymentReview';
import PaymentMethodEntry from './PaymentMethodEntry';
import { Main, Alert, Header, Review, Methods } from './index.styles';
import GoBack from './GoBack';

const PaymentCaptureMethod = () => {
  const paymentContext = useContext(PaymentContext);
  const { paymentErrors, raisePaymentError, clearPaymentErrors, isWebview, isFlexFlow } = paymentContext;

  const [nextStepDisabled, setNextStepDisabled] = useState(false);
  const transitionInFlightRef = useRef(false);
  const splits = useSplits([splitsList.preventDuplicatePayment]);
  const isGuardEnabled = splits[splitsList.preventDuplicatePayment];
  const trackUpsellStage = useTrackUpsellStage();

  const dispatch = useDispatch();

  useEffect(() => {
    if (paymentErrors.length) {
      upsellTransition({ stage: UpsellTransitionStages.UNLOCK });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [paymentErrors.length]);

  const lockNavigation = () => {
    upsellTransition({ stage: UpsellTransitionStages.LOCK });
    setNextStepDisabled(true);
    clearPaymentErrors();
  };

  const startTransition = () => {
    if (isGuardEnabled && transitionInFlightRef.current) {
      return false;
    }
    transitionInFlightRef.current = true;
    return true;
  };

  const endTransition = () => {
    transitionInFlightRef.current = false;
    setNextStepDisabled(false);
  };

  const handleSubmitAttempt = () => {
    trackUpsellStage(PAYMENT_LOG_STEPS.TOKENIZATION_STARTED);
    lockNavigation();
  };

  const handleSuccess = async (cardToken, paymentType) => {
    if (!startTransition()) {
      return;
    }

    if (!isFlexFlow) {
      trackSwitchFormAttempt(true, paymentType);
      trackPaymentMethodTokenizationResult(true, paymentType);
    }
    trackUpsellStage(PAYMENT_LOG_STEPS.SWITCH_FORM_COMPLETE);

    const runBeforeTransition = endTransition;

    try {
      await paymentMethodStoredTransition(
        paymentContext,
        { newMethodToken: cardToken.id, isAddingPaymentMethod: true, paymentType },
        runBeforeTransition
      )(dispatch);
    } catch (error) {
      endTransition();
      throw error;
    }
  };

  const handleFailure = (error, paymentType) => {
    endTransition();
    clearPaymentErrors();
    raisePaymentError({
      message: error.message,
      group: PAYMENT_ERROR_GROUPS.TOKENIZATION,
      type: PAYMENT_ERROR_TYPES.TOKENIZATION.GENERATING_PAYMENT_TOKEN,
    });

    if (!isFlexFlow) {
      trackSwitchFormAttempt(false, paymentType);
      trackPaymentMethodTokenizationResult(false, paymentType);
    }
    trackUpsellStage(PAYMENT_LOG_STEPS.SWITCH_FORM_FAILED);
    trackUpsellStage(PAYMENT_LOG_STEPS.PAYMENT_METHOD_TOKENIZATION_FAILED, {
      message: error.message,
      code: error.code,
      name: error.name,
      statusCode: error.statusCode,
      paymentMethod: paymentType,
    });
  };

  const handleExistingPaymentMethod = async (paymentMethodToken, paymentType) => {
    if (!startTransition()) {
      return;
    }

    lockNavigation();
    if (!isFlexFlow) {
      trackSwitchFormAttempt(true, paymentType);
      trackPaymentMethodTokenizationResult(true, paymentType);
    }
    trackUpsellStage(PAYMENT_LOG_STEPS.SWITCH_FORM_COMPLETE);

    const runBeforeTransition = endTransition;

    try {
      await paymentMethodStoredTransition(
        paymentContext,
        {
          newMethodToken: paymentMethodToken ?? null,
          isAddingPaymentMethod: false,
          paymentType,
        },
        runBeforeTransition
      )(dispatch);
    } catch (error) {
      endTransition();
      throw error;
    }
  };

  const paymentError = paymentErrors.length ? paymentErrors[paymentErrors.length - 1] : null;

  return (
    <React.Fragment>
      {!nextStepDisabled && !isWebview && <GoBack />}
      {paymentError && (
        <Alert key={paymentError.type} isWebview={isWebview}>
          <PaymentError error={paymentError} />
        </Alert>
      )}
      <Header>
        <PaymentHeading />
      </Header>
      <Main>
        <Review>
          <PaymentReview />
        </Review>
        <Methods>
          <PaymentMethodEntry
            disabled={nextStepDisabled}
            onSubmit={handleSubmitAttempt}
            onSuccess={handleSuccess}
            onFailure={handleFailure}
            onExistingPaymentMethod={handleExistingPaymentMethod}
          />
        </Methods>
      </Main>
    </React.Fragment>
  );
};

export default PaymentCaptureMethod;
